/* Generic CRUD component for the new Medical sub-tabs.
   Handles list + search + slide-panel form + edit + delete uniformly.
   Consumers provide: title, endpoint, columns [{key, label, format?}], formFields [{key, label, type, options?, span?, required?}], defaultForm, emptyText. */
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { canEdit } from "../../lib/permissions";
import { Card, Btn, Empty } from "../../components/UI";
import { SlidePanel, ConfirmDialog } from "../../components/SlidePanel";
import { FLabel, FInput, FSelect, FTextarea } from "../engagement/FormControls";
import { DateField } from "../engagement/DateField";
import { Plus, Search, Pencil, Trash2 } from "lucide-react";

export function GenericMedicalList({
  client, title, subtitle, endpointList, endpointCreate, endpointUpdate, endpointDelete,
  columns, formFields, defaultForm, emptyText, testPrefix, canRowEdit = null,
  extraActions = null, headerExtras = null,
}) {
  const { user } = useAuth();
  const editable = canEdit(user, "clinical");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(defaultForm);
  const [panel, setPanel] = useState(false);
  const [editId, setEditId] = useState(null);
  const [del, setDel] = useState(null);

  const load = () => {
    api.get(endpointList(client.id)).then((r) => setItems(Array.isArray(r.data) ? r.data : r.data?.items || []))
      .catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => { if (client?.id) load(); }, [client?.id]); // eslint-disable-line

  const filtered = useMemo(() => items.filter((it) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return columns.some((c) => (String(it[c.key] || "").toLowerCase().includes(s)));
  }), [items, q, columns]);

  const openAdd = () => { setForm(defaultForm); setEditId(null); setPanel(true); };
  const openEdit = (it) => { setForm({ ...defaultForm, ...it }); setEditId(it.id); setPanel(true); };

  const submit = async (e) => {
    e.preventDefault();
    for (const f of formFields) {
      if (f.required && !String(form[f.key] || "").trim()) {
        return toast.error(`${f.label} is required`);
      }
    }
    const payload = { ...form };
    try {
      if (editId) await api.put(endpointUpdate(editId), payload);
      else await api.post(endpointCreate(client.id), payload);
      toast.success(editId ? `${title} updated` : `${title} added`);
      setPanel(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const remove = async () => {
    try { await api.delete(endpointDelete(del.id)); toast.success(`${title} removed`); setDel(null); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <div data-testid={`${testPrefix}-panel`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-heading font-bold text-[#3E2723]">{title}</h3>
          {subtitle && <div className="text-[11px] text-[#7B6157]">{subtitle}</div>}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#7B6157]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
              data-testid={`${testPrefix}-search`}
              className="rounded-full border border-[#E5DFD3] bg-white pl-8 pr-3 py-1.5 text-xs outline-none w-56 focus:border-[#8D6E63]" />
          </div>
          {headerExtras}
          {editable && (
            <Btn variant="outline" data-testid={`${testPrefix}-add-btn`} onClick={openAdd}>
              <Plus size={13} /> Add
            </Btn>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <Empty text={emptyText || `No ${title.toLowerCase()} for this client`} />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[#EFEBE0]">
          <table className="w-full text-sm">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="bg-[#FDFCF8] border-b border-[#EFEBE0] text-[#4E342E] font-semibold py-2 px-3 text-left whitespace-nowrap">{c.label}</th>
                ))}
                <th className="bg-[#FDFCF8] border-b border-[#EFEBE0] py-2 px-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} data-testid={`${testPrefix}-row-${it.id}`} className="hover:bg-[#FDFCF8]">
                  {columns.map((c) => {
                    let v = it[c.key];
                    if (c.format === "date" && v) v = dayjs(v).format("MM/DD/YYYY");
                    if (c.format === "yesno") v = v ? "Yes" : "No";
                    if (c.format === "chip" && v) v = <span className="rounded-full bg-[#EFEBE0] px-2 py-0.5 text-[10px] font-bold text-[#6D4C41]">{v}</span>;
                    return <td key={c.key} className="border-b border-[#EFEBE0] py-2 px-3 text-[#3E2723]">{v || "-"}</td>;
                  })}
                  <td className="border-b border-[#EFEBE0] py-2 px-3">
                    <div className="flex justify-end gap-1">
                      {extraActions?.(it, load)}
                      {editable && (!canRowEdit || canRowEdit(it)) && (
                        <button type="button" data-testid={`${testPrefix}-edit-${it.id}`} onClick={() => openEdit(it)}
                          className="h-7 w-7 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Pencil size={12} /></button>
                      )}
                      {editable && (
                        <button type="button" data-testid={`${testPrefix}-delete-${it.id}`} onClick={() => setDel(it)}
                          className="h-7 w-7 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={12} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SlidePanel open={panel} onClose={() => setPanel(false)} title={editId ? `Edit ${title}` : `Add ${title}`} testId={`${testPrefix}-slide-panel`}>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {formFields.map((f) => (
              <div key={f.key} className={f.span === 2 ? "col-span-2" : ""}>
                <FLabel>{f.label}{f.required ? " *" : ""}</FLabel>
                {f.type === "select" ? (
                  <FSelect value={form[f.key] || ""} onChange={(e) => setF(f.key, e.target.value)} data-testid={`${testPrefix}-field-${f.key}`}>
                    <option value="">Select…</option>
                    {(f.options || []).map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
                  </FSelect>
                ) : f.type === "date" ? (
                  <DateField value={form[f.key] || ""} onChange={(v) => setF(f.key, v)} testId={`${testPrefix}-field-${f.key}`} />
                ) : f.type === "textarea" ? (
                  <FTextarea rows={f.rows || 2} value={form[f.key] || ""} onChange={(e) => setF(f.key, e.target.value)} data-testid={`${testPrefix}-field-${f.key}`} />
                ) : f.type === "number" ? (
                  <FInput type="number" step="any" value={form[f.key] ?? ""} onChange={(e) => setF(f.key, e.target.value === "" ? "" : Number(e.target.value))} data-testid={`${testPrefix}-field-${f.key}`} />
                ) : f.type === "checkbox" ? (
                  <label className="flex items-center gap-2 mt-2">
                    <input type="checkbox" checked={!!form[f.key]} onChange={(e) => setF(f.key, e.target.checked)}
                      data-testid={`${testPrefix}-field-${f.key}`} className="h-4 w-4 rounded border-[#E5DFD3] text-[#4E342E]" />
                    <span className="text-sm text-[#5D4037]">{f.checkboxLabel || f.label}</span>
                  </label>
                ) : (
                  <FInput value={form[f.key] || ""} onChange={(e) => setF(f.key, e.target.value)} data-testid={`${testPrefix}-field-${f.key}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-3">
            <Btn type="button" variant="ghost" onClick={() => setPanel(false)}>Cancel</Btn>
            <Btn data-testid={`${testPrefix}-save-btn`} type="submit">{editId ? "Save" : "Add"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <ConfirmDialog open={!!del} onCancel={() => setDel(null)} onConfirm={remove}
        title={`Delete ${title.toLowerCase()}`} description={del ? `Delete this ${title.toLowerCase()} record?` : ""} />
    </div>
  );
}
